/**
 * Spec 105 — PersonaFormModal. Create / new-version authoring form.
 *
 * Name, description, type select, confidence select, and a dimension editor
 * grouped by the five families. The wellbeing group is hidden/disabled when
 * type = commercial (FR-12 / Scenario 4 enforced server-side too).
 *
 * Used for BOTH create (no `base`) and new-version (`base` = the persona being
 * re-versioned; its content seeds the form).
 */
import { useMemo, useState } from 'react';
import { Button } from './ui/Button.js';
import type {
  Persona,
  PersonaConfidence,
  PersonaCreateInput,
  PersonaDimensionInput,
  PersonaDimensionType,
  PersonaType,
} from '../api/personas.js';
import {
  PERSONA_DIMENSION_TYPES,
  DIMENSION_LABELS,
  DIMENSION_HELP,
  suggestPersonaDimensions,
} from '../api/personas.js';
import type {
  PersonaSuggestionSource,
  PersonaSuggestionFamily,
  PersonaFallbackReason,
} from '../api/personas.js';

/**
 * Spec 243 — one sentence per reason, replacing spec 212's blanket "we couldn't
 * reach the drafting model".
 *
 * That clause was wrong in three of the four situations that produce a
 * template: with no key we never called, with an unusable answer we reached the
 * model fine, and an auth error is our configuration rather than the model's
 * availability. It was a causal claim the code could not support, written into
 * a banner whose whole purpose is not to overstate.
 *
 * Each sentence below says what happened and what to do about it, because those
 * differ: "try again" helps an outage and does nothing for a missing key.
 */
const FALLBACK_COPY: Record<PersonaFallbackReason, string> = {
  not_configured:
    'These are generic starter rows, not a reading of your description — drafting isn’t configured on this environment. Edit them to your persona.',
  provider_unavailable:
    'These are generic starter rows, not a reading of your description — the drafting model was unavailable just now. Editing them works, and so does trying again in a few minutes.',
  call_failed:
    'These are generic starter rows, not a reading of your description — the drafting request failed. Trying again may not help; if it keeps happening, it needs looking at.',
  unusable_response:
    'These are generic starter rows, not a reading of your description — the drafting model answered, but not in a form we could use. Trying again often works.',
};

interface DimRow extends PersonaDimensionInput {
  _key: string;
  /** Spec 111 — marks a row that came from an LLM suggestion (until edited away). */
  _suggested?: boolean;
}

let keyCounter = 0;
function newKey(): string {
  keyCounter += 1;
  return `dim-${keyCounter}`;
}

interface Props {
  brandId: string;
  base?: Persona | null; // present => new-version mode
  onClose: () => void;
  onSubmit: (input: PersonaCreateInput) => Promise<void>;
}

export function PersonaFormModal({ brandId, base, onClose, onSubmit }: Props) {
  const isNewVersion = !!base;
  const [name, setName] = useState(base?.name ?? '');
  const [description, setDescription] = useState(base?.description ?? '');
  const [personaType, setPersonaType] = useState<PersonaType>(
    base?.persona_type ?? 'commercial',
  );
  const [confidence, setConfidence] = useState<PersonaConfidence>(
    base?.confidence ?? 'hypothesis',
  );
  const [dims, setDims] = useState<DimRow[]>(() =>
    (base?.dimensions ?? []).map((d) => ({
      _key: newKey(),
      dimension_type: d.dimension_type,
      label: d.label,
      weight: d.weight,
      detail: d.detail ?? '',
      sort_order: d.sort_order,
    })),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Spec 111 — LLM-assist ("Suggest dimensions"). These NEVER touch confidence.
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  /**
   * Spec 212 — what the last draft actually was. Null until a draft is run.
   *
   * The suggester never throws for LLM-side problems: with no API key, or on any
   * provider failure, it returns a contract-conformant TEMPLATE. Before this,
   * those rows arrived looking exactly like a reading of the persona's
   * description, and nothing on screen distinguished them.
   */
  const [suggestProvenance, setSuggestProvenance] = useState<{
    source?: PersonaSuggestionSource;
    reason?: PersonaFallbackReason | null;
    placeholders: PersonaSuggestionFamily[];
  } | null>(null);

  const canSuggest = name.trim().length > 0 && description.trim().length > 0;

  const visibleFamilies = useMemo<PersonaDimensionType[]>(
    () =>
      PERSONA_DIMENSION_TYPES.filter(
        (f) => !(f === 'wellbeing' && personaType === 'commercial'),
      ),
    [personaType],
  );

  function addDim(type: PersonaDimensionType) {
    setDims((prev) => [
      ...prev,
      {
        _key: newKey(),
        dimension_type: type,
        label: '',
        weight: 1.0,
        detail: '',
        sort_order: prev.length,
      },
    ]);
  }

  function updateDim(key: string, patch: Partial<DimRow>) {
    setDims((prev) =>
      // Editing a suggested row clears its "suggested" marker (it's now author-owned).
      prev.map((d) =>
        d._key === key ? { ...d, ...patch, _suggested: false } : d,
      ),
    );
  }

  function removeDim(key: string) {
    setDims((prev) => prev.filter((d) => d._key !== key));
  }

  /**
   * Spec 111 — fetch LLM-drafted dimensions and APPEND them as editable rows.
   * HARD CONSTRAINT: this path NEVER calls setConfidence — suggestions are
   * hypothesis-grade drafts and must not change the author-asserted grade.
   */
  async function handleSuggest() {
    if (!canSuggest || suggesting) return;
    setSuggestError(null);
    setSuggestProvenance(null);
    setSuggesting(true);
    try {
      const result = await suggestPersonaDimensions(brandId, {
        name: name.trim(),
        description: description.trim(),
        persona_type: personaType,
      });
      const families: PersonaDimensionType[] = [
        'priority',
        'trust_signal',
        'anti_signal',
        'wellbeing',
        'query_disposition',
      ];
      const added: DimRow[] = [];
      for (const family of families) {
        // Drop wellbeing on commercial personas regardless of server output.
        if (family === 'wellbeing' && personaType === 'commercial') continue;
        for (const s of result[family] ?? []) {
          if (!s.label?.trim()) continue;
          added.push({
            _key: newKey(),
            _suggested: true,
            dimension_type: family,
            label: s.label,
            weight: typeof s.weight === 'number' ? s.weight : 1.0,
            detail: s.detail ?? '',
            sort_order: 0,
          });
        }
      }
      if (added.length === 0) {
        setSuggestError('No suggestions were returned. Try refining the description.');
        return;
      }
      setSuggestProvenance({
        source: result.source,
        reason: result.fallback_reason ?? null,
        placeholders: result.placeholder_families ?? [],
      });
      setDims((prev) => [...prev, ...added]);
      // NOTE: confidence intentionally untouched (FR-111-10).
    } catch (e) {
      setSuggestError(
        e instanceof Error ? e.message : 'Failed to suggest dimensions',
      );
    } finally {
      setSuggesting(false);
    }
  }

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const priorityCount = dims.filter(
      (d) => d.dimension_type === 'priority' && d.label.trim(),
    ).length;
    if (priorityCount === 0) {
      setError('At least one priority dimension is required');
      return;
    }
    if (
      personaType === 'commercial' &&
      dims.some((d) => d.dimension_type === 'wellbeing')
    ) {
      setError('Wellbeing dimensions are not allowed on a commercial persona');
      return;
    }
    const input: PersonaCreateInput = {
      name: name.trim(),
      description: description.trim() || null,
      persona_type: personaType,
      confidence,
      dimensions: dims
        .filter((d) => d.label.trim())
        .map((d, i) => ({
          dimension_type: d.dimension_type,
          label: d.label.trim(),
          weight: typeof d.weight === 'number' ? d.weight : 1.0,
          detail: (d.detail ?? '').toString().trim() || null,
          sort_order: i,
        })),
    };
    setSubmitting(true);
    try {
      await onSubmit(input);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save persona');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="persona-form-modal"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
    >
      <div className="mt-8 w-full max-w-2xl rounded-lg bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold">
          {isNewVersion
            ? `New version of "${base?.name}" (from v${base?.version})`
            : 'New persona'}
        </h2>

        {error && (
          <div
            data-testid="persona-form-error"
            className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="mb-1 block text-sm font-medium">Name</label>
          <input
            data-testid="persona-name-input"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Procurement Lead"
          />
        </div>

        <div className="mb-3">
          <label className="mb-1 block text-sm font-medium">Description</label>
          <textarea
            data-testid="persona-description-input"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            rows={2}
            value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Spec 111 — LLM-assist. Enabled once Name + Description are present.
            Suggestions are hypothesis-grade drafts and never change Confidence. */}
        <div className="mb-3 flex items-center gap-3">
          <Button
            variant="ghost"
            data-testid="persona-suggest-dimensions"
            onClick={handleSuggest}
            disabled={!canSuggest || suggesting}
            title={
              canSuggest
                ? 'Draft candidate dimensions from the name + description'
                : 'Enter a name and description first'
            }
          >
            {suggesting ? 'Suggesting…' : '✨ Suggest dimensions'}
          </Button>
          <span className="text-xs text-gray-400">
            Drafts editable rows — review &amp; edit before saving. Does not
            change Confidence.
          </span>
        </div>
        {/* Spec 212 — say where the draft came from, in plain words. Rendered
            only when there is something to disclose: a clean live draft needs
            no banner, and one on every draft would train the eye to skip it. */}
        {suggestProvenance?.source === 'fallback' && (
          // Spec 248: this was a filled amber panel — Kridt forbids exactly
          // that — shipped in specs 212 and 243. Same testid and words, so
          // 212.A.4 and 243.A.5 still assert it; only the treatment changed.
          <div data-testid="persona-suggest-fallback" className="hub-banner mb-3" data-tone="warning" role="status">
            <i className="bx bx-error hub-banner__icon" aria-hidden="true" />
            <p className="hub-banner__body m-0">
            {/* An older server sends no reason; say the part that is true in
                every case rather than guessing which one applies. */}
            {(suggestProvenance.reason && FALLBACK_COPY[suggestProvenance.reason]) ??
              'These are generic starter rows, not a reading of your description. Edit them to your persona.'}
            </p>
          </div>
        )}
        {suggestProvenance?.source === 'model' &&
          suggestProvenance.placeholders.length > 0 && (
            <div data-testid="persona-suggest-placeholders" className="hub-banner mb-3" data-tone="info" role="status">
              <i className="bx bx-info-circle hub-banner__icon" aria-hidden="true" />
              <p className="hub-banner__body m-0">
              The {suggestProvenance.placeholders.join(', ').replace(/_/g, ' ')}{' '}
              rows are generic placeholders — the model didn&apos;t return any, so
              we filled them in. The rest were drafted from your description.
              </p>
            </div>
          )}
        {suggestError && (
          <div
            data-testid="persona-suggest-error"
            className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700"
          >
            {suggestError}
          </div>
        )}

        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium">Type</label>
            <select
              data-testid="persona-type-select"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={personaType}
              onChange={(e) => setPersonaType(e.target.value as PersonaType)}
            >
              <option value="commercial">Commercial</option>
              <option value="informational">Informational</option>
            </select>
            <p
              data-testid="persona-help-type"
              className="mt-1 text-xs text-gray-500"
            >
              Informational adds a separate Wellbeing family.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Confidence</label>
            <select
              data-testid="persona-confidence-select"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={confidence}
              onChange={(e) =>
                setConfidence(e.target.value as PersonaConfidence)
              }
            >
              <option value="survey_grounded">Survey-grounded</option>
              <option value="research_informed">Research-informed</option>
              <option value="hypothesis">Hypothesis</option>
            </select>
            <p
              data-testid="persona-help-confidence"
              className="mt-1 text-xs text-gray-500"
            >
              How well-grounded this persona is. Author-asserted in v1: choose
              Hypothesis unless backed by real research/survey data — downstream
              diagnoses cap their confidence at this grade.
            </p>
          </div>
        </div>

        <div className="mb-4 space-y-4">
          {visibleFamilies.map((family) => {
            const rows = dims.filter((d) => d.dimension_type === family);
            return (
              <div key={family} className="rounded-md border border-gray-200 p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-semibold">
                    {DIMENSION_LABELS[family]}
                    {family === 'priority' && (
                      <span className="ml-1 text-xs text-red-500">
                        (≥1 required)
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    data-testid={`persona-dimension-add-${family}`}
                    className="hub-btn hub-btn--ghost"
                    onClick={() => addDim(family)}
                  >
                    + Add
                  </button>
                </div>
                <p
                  data-testid={`persona-help-${family}`}
                  className="mb-2 text-xs text-gray-500"
                >
                  {DIMENSION_HELP[family]}
                </p>
                {rows.length === 0 && (
                  <p className="text-xs text-gray-400">No dimensions yet.</p>
                )}
                {rows.length > 0 && (
                  <p
                    data-testid={`persona-help-weight-${family}`}
                    className="mb-2 text-xs text-gray-400"
                  >
                    Weight: relative importance when scoring (default 1.0; higher
                    = counts more).
                  </p>
                )}
                {rows.map((d) => (
                  <div
                    key={d._key}
                    data-testid="persona-dimension-row"
                    data-suggested={d._suggested ? 'true' : 'false'}
                    className={`mb-2 grid grid-cols-12 gap-2 ${
                      d._suggested ? 'rounded-md bg-indigo-50/60 p-1' : ''
                    }`}
                  >
                    <input
                      className="col-span-5 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      placeholder="Label"
                      value={d.label}
                      onChange={(e) =>
                        updateDim(d._key, { label: e.target.value })
                      }
                    />
                    <input
                      type="number"
                      step="0.1"
                      className="col-span-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      placeholder="Weight"
                      value={d.weight ?? 1.0}
                      onChange={(e) =>
                        updateDim(d._key, {
                          weight: parseFloat(e.target.value),
                        })
                      }
                    />
                    <input
                      className="col-span-4 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      placeholder="Detail (optional)"
                      value={(d.detail ?? '') as string}
                      onChange={(e) =>
                        updateDim(d._key, { detail: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className="col-span-1 rounded-md text-sm text-red-500 hover:text-red-700"
                      onClick={() => removeDim(d._key)}
                      aria-label="Remove dimension"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" data-testid="persona-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" data-testid="persona-save" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : isNewVersion ? 'Save new version' : 'Create persona'}
          </Button>
        </div>
      </div>
    </div>
  );
}
